import * as React from "react";
import {
    FindDealerCords,
    FindDealerCustomer,
    FindDealerFilters,
    FindDealerMapOptions,
    FindDealerProps,
    FindDealerSetDealersFromLocation,
    FindDealerSetLocationFromCurrentPosition,
    FindDealerSetLocationFromZipcode,
} from "./FindDealer.types";
import { GoogleMap, useJsApiLoader } from "@react-google-maps/api";
import {
    useAxios,
    useGeolocation,
    useIsMounted,
    useMediaQuery,
    useSettings,
    useUpdateEffect,
} from "@Tmp/Hooks";
import clsx from "clsx";
import Filters from "./Filters";
import MarkerCurrentPosition from "./MarkerCurrentPosition";
import FindDealerMarker from "./Marker";
import List from "./List";
import Dialog from "@Tmp/Dialog";
import Toolbar from "./Toolbar";
import { getParam } from "@TmpApp/Utils";
import { SkeletonLoading } from "..";

const containerStyle = {
    width: "100%",
    height: "100%",
};

const googleMapsLibraries: ["geometry"] = ["geometry"];

export default React.memo(function FindDealer({ ...props }: FindDealerProps) {
    const tmpSettings = useSettings();
    const [map, setMap] = React.useState<google.maps.Map>(null);
    const mobileList = useMediaQuery("(max-width:899px)");
    const [zipcode, setZipcode] = React.useState("");
    const [tooltipOpen, setTooltipOpen] = React.useState<null | number>(null);
    const [currentPositionTooltipOpen, setCurrentPositionTooltipOpen] = React.useState(true);
    const [filters, setFilters] = React.useState<FindDealerFilters>(
        props.settings.filter_boxes?.map?.((item) => ({
            repeater_index: item._id,
            active: false,
            country_id: item.filter_box_country_id,
            customer_type_id: item.filter_box_customer_type_id,
        })) ?? []
    );
    const [mapOptions, setMapOptions] = React.useState<FindDealerMapOptions>({
        center: {
            lat: props.settings.map_center?.lat ?? 56.26392,
            lng: props.settings.map_center?.lng ?? 9.501785,
        },
        zoom: props.settings?.map_zoom?.size ?? 9,
        nearestDealersCount: props.settings?.find_dealer_nearest_dealers_count?.size ?? 3,
    });
    const [listOpen, setListOpenDispatch] = React.useState(false);

    const setListOpen: React.Dispatch<React.SetStateAction<boolean>> = (value) => {
        setListOpenDispatch(value);
        if (value) {
            jQuery("html, body").addClass("tmp-find-dealer-list-modal-active");
        } else {
            jQuery("html, body").removeClass("tmp-find-dealer-list-modal-active");
        }
    };

    const actualListElement = ".tmp-find-dealer__list__" + props?.id;
    const listContainerElement = ".tmp-find-dealer__list__container-" + props?.id;
    const listElement = mobileList ? actualListElement : listContainerElement;

    const { isLoaded } = useJsApiLoader({
        id: props.id,
        googleMapsApiKey: tmpSettings.google_maps_api_key,
        libraries: googleMapsLibraries,
    });

    const {
        loading: loadingDealers,
        dealers,
        setDealersFromLocation,
        setDealerFromQueryParam,
    } = useDealers({
        map,
        filters,
        mapOptions,
        setMapOptions,
        setTooltipOpen,
        onFetched: () => {
            calculateClosestDealer();
        },
        ...props,
    });

    const { location, isLocationDenied, setLocationFromZipcode, setLocationFromCurrentPosition } =
        useCurrentLocation({
            listElement,
            setMapOptions,
            setDealersFromLocation,
            setCurrentPositionTooltipOpen,
            ...props,
        });

    useUpdateEffect(() => {
        if (loadingDealers) {
            return;
        }

        if (dealers.filter((dealer) => dealer.distance).length < 1) {
            return;
        }

        setDefaultSelectedDealer(dealers[0].id);
    }, [dealers, loadingDealers]);

    useUpdateEffect(() => {
        if (!map || !mapOptions.center) {
            return;
        }

        map.panTo(mapOptions.center);
    }, [map, mapOptions.center]);

    const calculateClosestDealer = () => {
        if (getParam("selected-dealer")) {
            setDealerFromQueryParam();
        } else {
            if (zipcode) {
                setLocationFromZipcode(zipcode);
            } else {
                setLocationFromCurrentPosition();
            }
        }
    };

    const setDefaultSelectedDealer = (dealerId: number) => {
        if (props?.selectedDealer === null && props?.setSelectedDealer) {
            props.setSelectedDealer(dealerId);

            if (props?.onDefaultSelectedDealer) {
                props.onDefaultSelectedDealer(dealerId);
            }
        }
    };

    const handleLoad = React.useCallback((map: google.maps.Map) => {
        setMap(map);
    }, []);

    const handleUnmount = React.useCallback((map: google.maps.Map) => {
        setMap(null);
    }, []);

    const googleMapOptions = React.useMemo(
        () => ({
            gestureHandling: "greedy",
        }),
        []
    );

    return (
        <div className="tmp-find-dealer__container">
            <Toolbar
                isLocationDenied={isLocationDenied}
                zipcode={zipcode}
                setZipcode={setZipcode}
                settings={props?.settings}
                setLocationFromZipcode={setLocationFromZipcode}
                setLocationFromCurrentPosition={setLocationFromCurrentPosition}
            />
            <div
                className={clsx(
                    "tmp-find-dealer__list__container",
                    "tmp-find-dealer__list__container-" + props?.id
                )}
            >
                <div
                    className="tmp-find-dealer__list-modal__button"
                    onClick={() => {
                        setListOpen(!listOpen);
                    }}
                >
                    {props.settings.find_dealer_open_dealer_list_text}
                </div>
                {mobileList ? (
                    <Dialog
                        open={listOpen}
                        setOpen={setListOpen}
                        header={<h3>{props.settings?.find_dealer_dealer_list_modal_title}</h3>}
                        classes={{
                            container: "tmp-find-dealer__list-modal__container",
                        }}
                    >
                        <List
                            id={props.id}
                            settings={props.settings}
                            loadingDealers={loadingDealers}
                            dealers={dealers}
                            setMapOptions={setMapOptions}
                            activeDealer={tooltipOpen}
                            setActiveDealer={setTooltipOpen}
                            setListOpen={setListOpen}
                            selectedDealer={props?.selectedDealer}
                            setSelectedDealer={props?.setSelectedDealer}
                        />
                    </Dialog>
                ) : (
                    <List
                        id={props.id}
                        settings={props.settings}
                        loadingDealers={loadingDealers}
                        dealers={dealers}
                        setMapOptions={setMapOptions}
                        activeDealer={tooltipOpen}
                        setActiveDealer={setTooltipOpen}
                        setListOpen={setListOpen}
                        selectedDealer={props?.selectedDealer}
                        setSelectedDealer={props?.setSelectedDealer}
                    />
                )}
            </div>
            <div className="tmp-find-dealer__map__container">
                {!isLoaded ? (
                    <SkeletonLoading
                        animation="wave"
                        width={containerStyle.width}
                        height={containerStyle.height}
                    />
                ) : (
                    <GoogleMap
                        mapContainerStyle={containerStyle}
                        zoom={mapOptions.zoom}
                        onLoad={handleLoad}
                        onUnmount={handleUnmount}
                        options={googleMapOptions}
                    >
                        <MarkerCurrentPosition
                            settings={props.settings}
                            location={location}
                            currentPositionTooltipOpen={currentPositionTooltipOpen}
                            setCurrentPositionTooltipOpen={setCurrentPositionTooltipOpen}
                        />
                        {dealers.map((dealer, index) => (
                            <FindDealerMarker
                                settings={props?.settings}
                                key={index}
                                index={index}
                                mapOptions={mapOptions}
                                setListOpen={setListOpen}
                                currentLocation={location}
                                listElement={listElement}
                                dealer={dealer}
                                activeDealer={tooltipOpen}
                                setActiveDealer={setTooltipOpen}
                                selectedDealer={props?.selectedDealer}
                                setSelectedDealer={props?.setSelectedDealer}
                            />
                        ))}
                    </GoogleMap>
                )}
                {props.settings.enable_filter_boxes === "yes" && (
                    <Filters settings={props.settings} filters={filters} setFilters={setFilters} />
                )}
            </div>
        </div>
    );
});

type UseCurrentLocationProps = FindDealerProps & {
    listElement: string;
    setMapOptions: React.Dispatch<React.SetStateAction<FindDealerMapOptions>>;
    setDealersFromLocation: FindDealerSetDealersFromLocation;
    setCurrentPositionTooltipOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

function useCurrentLocation({
    listElement,
    setMapOptions,
    setDealersFromLocation,
    setCurrentPositionTooltipOpen,
    ...props
}: UseCurrentLocationProps) {
    const [location, setLocation] = React.useState<FindDealerCords>();
    const { data, error, run } = useGeolocation();

    const isLocationDenied = React.useMemo(() => !!error, [error]);

    const handleCurrentPosition = React.useCallback((position: FindDealerCords) => {
        if (typeof position?.lat !== "number" || typeof position?.lng !== "number") {
            return;
        }

        setMapOptions((prev) => {
            return { ...prev, center: position };
        });
        setLocation(position);
        setDealersFromLocation(position);
        setCurrentPositionTooltipOpen(true);

        jQuery(listElement).stop().animate(
            {
                scrollTop: 0,
            },
            750
        );

        tmp.gtag("event", "map_current_position", {
            lat: position.lat,
            lng: position.lng,
        });
    }, []);

    const setLocationFromZipcode = React.useCallback<FindDealerSetLocationFromZipcode>((value) => {
        const geo = new google.maps.Geocoder();

        geo.geocode(
            {
                address: "zipcode " + value + " Danmark",
            },
            function (results, status) {
                if (status == google.maps.GeocoderStatus.OK) {
                    const rawLocation = results?.[0]?.geometry?.location;

                    const location = { lat: rawLocation?.lat?.(), lng: rawLocation?.lng?.() };

                    handleCurrentPosition(location);
                }
            }
        );
    }, []);

    const setLocationFromCurrentPosition =
        React.useCallback<FindDealerSetLocationFromCurrentPosition>(() => {
            run();
            handleCurrentPosition(data);
        }, [data]);

    useUpdateEffect(() => {
        if (location) {
            return;
        }

        setLocationFromCurrentPosition();
    }, [data]);

    return { location, isLocationDenied, setLocationFromZipcode, setLocationFromCurrentPosition };
}

type UseDealersProps = FindDealerProps & {
    map: google.maps.Map;
    filters: FindDealerFilters;
    onFetched?: (dealers: FindDealerCustomer[]) => void;
    mapOptions: FindDealerMapOptions;
    setMapOptions: React.Dispatch<React.SetStateAction<FindDealerMapOptions>>;
    setTooltipOpen: React.Dispatch<React.SetStateAction<number>>;
};

function useDealers({
    map,
    mapOptions,
    filters,
    onFetched,
    setTooltipOpen,
    setMapOptions,
    ...props
}: UseDealersProps) {
    const isMounted = useIsMounted();
    const tmpSettings = useSettings();

    const justFetched = React.useRef(false);
    const [loading, setLoading] = React.useState(true);
    const [dealers, setDealers] = React.useState<FindDealerCustomer[]>([]);

    const getCustomerTypeIds = React.useCallback(() => {
        let customerTypeIds = [];

        filters
            .filter((item) => item.active && item.customer_type_id)
            .map((item) => (customerTypeIds = customerTypeIds.concat(item.customer_type_id)));

        return customerTypeIds;
    }, [filters]);

    const { status: fetchStatus, value: fetchValue } = useAxios(
        "get",
        "/wp-content/plugins/tmp/api/dealers.php",
        {
            page_id: tmpSettings.page_id,
            widget_id: props.id,
            country_ids: filters
                .filter((item) => item.active && item.country_id)
                .map((item) => item.country_id),
            customer_type_ids: getCustomerTypeIds(),
        },
        [filters]
    );

    useUpdateEffect(() => {
        if (fetchStatus === "success") {
            if (fetchValue.status === 200 && isMounted()) {
                const fetchedDealers = fetchValue.data.dealers
                    ?.map?.((dealer) => {
                        dealer.tooltipOpen = false;
                        dealer.element = { current: null };
                        return dealer;
                    })
                    .sort((a, b) => {
                        if (a.zipcode > b.zipcode) {
                            return 1;
                        }

                        if (a.zipcode < b.zipcode) {
                            return -1;
                        }

                        return 0;
                    });
                justFetched.current = true;
                setDealers(fetchedDealers);
            }
        }
        if (fetchStatus !== "loading") {
            setLoading(false);
        }
    }, [fetchStatus]);

    useUpdateEffect(() => {
        if (justFetched.current) {
            justFetched.current = false;
            onFetched(dealers);
        }
    }, [dealers]);

    const setDealerFromQueryParam = React.useCallback(() => {
        const dealerId = parseInt(getParam("selected-dealer"));

        setTooltipOpen(dealerId);

        const dealerElement = dealers.find((item) => item.id === dealerId);

        if (dealerElement) {
            const dealer = dealerElement;
            const el = jQuery(dealer?.element?.current);
            const listEl = jQuery(".tmp-find-dealer-list.tmp-find-dealer-list-" + props?.id);

            listEl.stop().animate(
                {
                    scrollTop:
                        el.offset().top -
                        el.parent().offset().top -
                        (listEl.height() / 2 - el.height() / 2),
                },
                750
            );

            setMapOptions((prev) => {
                return { ...prev, zoom: 9, center: dealer.address.coordinates };
            });
        }
    }, [dealers]);

    const setDealersFromLocation = React.useCallback<FindDealerSetDealersFromLocation>(
        (location) => {
            if (typeof location?.lat !== "number" || typeof location?.lng !== "number") {
                return;
            }

            setDealers((prev) => {
                const newDealers = [...prev];
                newDealers.map((dealer) => {
                    const dealerPosition = new google.maps.LatLng(dealer.address.coordinates);

                    const locationPosition = new google.maps.LatLng(location);

                    dealer.distance = google.maps.geometry.spherical.computeDistanceBetween(
                        locationPosition,
                        dealerPosition
                    );

                    return dealer;
                });
                newDealers.sort((a, b) => a.distance - b.distance);
                if (newDealers[0]) {
                    const dealer = newDealers[0];
                    setTooltipOpen(dealer.id);

                    tmp.gtag("event", "map_closest_location", {
                        lat: location.lat,
                        lng: location.lng,
                        location_lat: dealer.address.coordinates.lat,
                        location_lng: dealer.address.coordinates.lng,
                        location_name: dealer.name,
                        distance_to_location_km: dealer.distance / 1000,
                        dealer_account: dealer.account,
                    });
                }
                resolveBoundsFromDealersDistance(location, [...newDealers]);
                return newDealers;
            });
        },
        []
    );

    const resolveBoundsFromDealersDistance = React.useCallback(
        (location: FindDealerCords, allDealers: FindDealerCustomer[]) => {
            if (!map) {
                return;
            }

            const bounds = new google.maps.LatLngBounds();
            const nearestDealers = allDealers.splice(0, mapOptions.nearestDealersCount);

            if (nearestDealers.length < 1) {
                return;
            }

            bounds.extend(location);

            nearestDealers.map((dealer) => bounds.extend(dealer.address.coordinates));

            map.fitBounds(bounds);
        },
        [map]
    );

    return { loading, dealers, setDealersFromLocation, setDealerFromQueryParam };
}
